/**
 * Which kind of reproduction to create: logo, image or gradient.
 *
 * The three differ in what is uploaded and how colour is assigned - a logo maps
 * its elements to palette colours, an image is gamut-mapped and halftoned, a
 * gradient blends between chosen palette colours inside a shape - so the choice
 * is made up front and cannot be changed afterwards.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { Stack, Typography, Card, CardActionArea, CardContent, Box } from "@mui/material";
import PaletteIcon from "@mui/icons-material/Palette";
import ImageIcon from "@mui/icons-material/Image";
import GradientIcon from "@mui/icons-material/Gradient";
import ExplorationPageShell from "../../components/ExplorationPageShell";

// Entry of the reproduction module: pick the kind of reproduction. Each card
// opens the list of that kind (/colorreproductions/<kind>), the same pages the
// side menu's Reproductions entries lead to.
export default function ColorReproductionTypeChoice() {
    const navigate = useNavigate();

    // The combined list holds every kind; the choice opens the create page of the chosen one.
    const chooseLogoReproduction = () => navigate("/colorreproductions/logo/new");
    const chooseImageReproduction = () => navigate("/colorreproductions/image/new");
    const chooseGradientReproduction = () => navigate("/colorreproductions/gradient/new");

    return (
        <Stack alignItems="center" sx={{ mt: "2%", mb: 4, width: "100%" }} spacing={2}>
            <ExplorationPageShell
                title="Reproduction"
                subtitle="Select the kind of reproduction to run."
            >
                <Box
                    sx={{
                        display: "grid",
                        gap: 2.5,
                        gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                    }}
                >
                    <Card
                        variant="outlined"
                        sx={{
                            borderRadius: 3,
                            borderColor: "rgba(15, 23, 42, 0.08)",
                            transition: "box-shadow 0.2s ease, transform 0.2s ease",
                            "&:hover": {
                                boxShadow: "0 10px 30px rgba(15, 23, 42, 0.08)",
                                transform: "translateY(-2px)",
                            },
                        }}
                    >
                        <CardActionArea onClick={chooseLogoReproduction} sx={{ height: "100%" }}>
                            <CardContent>
                                <Stack direction="column" alignItems="center" spacing={2} sx={{ py: 3 }}>
                                    <PaletteIcon sx={{ fontSize: 56 }} color="primary" />
                                    <Typography variant="h6">Logo Reproduction</Typography>
                                    <Typography variant="body2" color="text.secondary" align="center">
                                        Upload a vector logo, map its components onto a marked
                                        color palette and generate the marking file.
                                    </Typography>
                                </Stack>
                            </CardContent>
                        </CardActionArea>
                    </Card>
                    <Card
                        variant="outlined"
                        sx={{
                            borderRadius: 3,
                            borderColor: "rgba(15, 23, 42, 0.08)",
                            transition: "box-shadow 0.2s ease, transform 0.2s ease",
                            "&:hover": {
                                boxShadow: "0 10px 30px rgba(15, 23, 42, 0.08)",
                                transform: "translateY(-2px)",
                            },
                        }}
                    >
                        <CardActionArea onClick={chooseImageReproduction} sx={{ height: "100%" }}>
                            <CardContent>
                                <Stack direction="column" alignItems="center" spacing={2} sx={{ py: 3 }}>
                                    <ImageIcon sx={{ fontSize: 56 }} color="primary" />
                                    <Typography variant="h6">Image Reproduction</Typography>
                                    <Typography variant="body2" color="text.secondary" align="center">
                                        Reproduce full images with halftoning on top of a
                                        characterized primaries set.
                                    </Typography>
                                </Stack>
                            </CardContent>
                        </CardActionArea>
                    </Card>
                    <Card
                        variant="outlined"
                        sx={{
                            borderRadius: 3,
                            borderColor: "rgba(15, 23, 42, 0.08)",
                            transition: "box-shadow 0.2s ease, transform 0.2s ease",
                            "&:hover": {
                                boxShadow: "0 10px 30px rgba(15, 23, 42, 0.08)",
                                transform: "translateY(-2px)",
                            },
                        }}
                    >
                        <CardActionArea onClick={chooseGradientReproduction} sx={{ height: "100%" }}>
                            <CardContent>
                                <Stack direction="column" alignItems="center" spacing={2} sx={{ py: 3 }}>
                                    <GradientIcon sx={{ fontSize: 56 }} color="primary" />
                                    <Typography variant="h6">Gradient Reproduction</Typography>
                                    <Typography variant="body2" color="text.secondary" align="center">
                                        Fill a shape with a halftoned blend that runs through
                                        palette colors of your choice.
                                    </Typography>
                                </Stack>
                            </CardContent>
                        </CardActionArea>
                    </Card>
                </Box>
            </ExplorationPageShell>
        </Stack>
    );
}
